import { genericDatepickerInstances } from '../builders/GenericDatepickerRegistry.js';
import { Constants } from '../shared/Constants.js';
import { formatDisplayDate, syncInputValue } from '../features/formatting/displayValue.js';
import { placePopup } from '../features/popup/placePopup.js';
import {
  resolveInitialState,
  parseRangeValue,
  getRangePayload,
  selectRangeDate,
} from '../features/range/rangeState.js';
import { renderHeader, navigate } from '../features/calendar/renderHeader.js';
import { renderDays } from '../features/calendar/renderDays.js';
import { renderMonths } from '../features/calendar/renderMonths.js';
import { renderYears } from '../features/calendar/renderYears.js';
import { renderTime } from '../features/time/renderTime.js';

let _gdpCounter = 0;
let _activeInstance = null;

/**
 * Runtime datepicker widget — lifecycle, orchestration, and public API.
 * Render/selection details live under features/.
 */
export class GenericDatepicker {
  constructor(selector, config) {
    const $ = window.jQuery;
    this.id = ++_gdpCounter;
    this.namespace = `.gdp_${this.id}`;
    this.$input = $(selector);
    this.config = config;
    const initial = resolveInitialState(config);
    this.state = {
      currentDate: initial.currentDate,
      viewDate: initial.currentDate ? new Date(initial.currentDate) : (window.GenericDateTime?.now().toDate() ?? new Date()),
      view: this._getInitialView(),
      isOpen: false,
      showTime: !config.timeToggle,
      startDate: initial.startDate,
      endDate: initial.endDate,
      rangeStep: initial.rangeStep,
      hoverDate: null,
    };
    this.$container = null;
    this._init();
  }

  _resolveInitialState(config) {
    return resolveInitialState(config);
  }

  _parseRangeValue(value, fallback = new Date()) {
    return parseRangeValue(value, fallback);
  }

  _getRangePayload() {
    return getRangePayload(this);
  }

  _formatDisplayDate(date) {
    return formatDisplayDate(this, date);
  }

  _syncInputValue() {
    syncInputValue(this);
  }

  _init() {
    const $ = window.jQuery;
    this.$input.addClass('gdp-input').attr('readonly', true);
    this.$input.on(`click${this.namespace}`, () => {
      this.toggle();
    });

    $(document).on(`click${this.namespace}`, (e) => {
      if (!this.state.isOpen) return;
      const $target = $(e.target);
      if (!$target.closest('.gdp-container').length && !$target.is(this.$input)) {
        this.hide();
      }
    });

    this._syncInputValue();
  }

  _getInitialView() {
    if (this.config.components.date) return Constants.Views.DAYS;
    if (this.config.components.month) return Constants.Views.MONTHS;
    if (this.config.components.year) return Constants.Views.YEARS;
    return Constants.Views.TIME;
  }

  toggle() {
    if (this.state.isOpen) this.hide();
    else this.show();
  }

  show() {
    if (this.state.isOpen) return;

    if (_activeInstance && _activeInstance !== this) {
      _activeInstance.hide();
    }
    _activeInstance = this;

    this._render();
    this.state.isOpen = true;
    this.$container.addClass('show');

    if (this.config.onOpen) this.config.onOpen(this);
  }

  hide() {
    if (!this.state.isOpen) return;

    if (_activeInstance === this) {
      _activeInstance = null;
    }

    this.$container.removeClass('show');
    setTimeout(() => {
      if (this.$container) {
        this.$container.remove();
        this.$container = null;
      }
    }, 300);

    this.state.isOpen = false;
    if (this.config.onClose) this.config.onClose(this);
  }

  _render() {
    this.$container = placePopup(this);
    this._updateContent();
  }

  _updateContent() {
    const $ = window.jQuery;
    const $content = $('<div class="gdp-content"></div>');

    $content.append(renderHeader(this));

    const $body = $('<div class="gdp-body"></div>');
    switch (this.state.view) {
      case Constants.Views.DAYS:
        $body.append(renderDays(this));
        break;
      case Constants.Views.MONTHS:
        $body.append(renderMonths(this));
        break;
      case Constants.Views.YEARS:
        $body.append(renderYears(this));
        break;
      case Constants.Views.TIME:
        $body.append(renderTime(this));
        break;
      default:
        break;
    }
    $content.append($body);

    const hasTime = this.config.components.hours || this.config.components.minutes;
    const isDayView = this.state.view === Constants.Views.DAYS;

    if (hasTime && isDayView) {
      if (this.config.timeToggle) {
        const $toggle = $(
          `<button type="button" class="gdp-time-toggle ${this.state.showTime ? 'active' : ''}">
            <i class="bi bi-clock"></i> <span>${this.state.showTime ? 'Hide Time' : 'Set Time'}</span>
            <i class="bi bi-chevron-down gdp-toggle-icon"></i>
          </button>`,
        );
        $toggle.on('click', (e) => {
          e.stopPropagation();
          this.state.showTime = !this.state.showTime;
          this._updateContent();
        });
        $content.append($toggle);
      }

      if (this.state.showTime) {
        const $timeContainer = $('<div class="gdp-time-inline"></div>').append(renderTime(this));
        $content.append($timeContainer);
      }
    }

    if (hasTime || this.config.range) {
      $content.append(this._renderFooter());
    }

    this.$container.empty().append($content);
  }

  _renderHeader() {
    return renderHeader(this);
  }

  _navigate(dir) {
    navigate(this, dir);
  }

  _renderDays() {
    return renderDays(this);
  }

  _renderMonths() {
    return renderMonths(this);
  }

  _renderYears() {
    return renderYears(this);
  }

  _renderTime() {
    return renderTime(this);
  }

  _renderFooter() {
    const $ = window.jQuery;
    const $footer = $('<div class="gdp-footer"></div>');
    const hasTime = this.config.components.hours || this.config.components.minutes;

    if (this.config.range) {
      const sep = this.config.rangeSeparator ?? ' – ';
      let status = 'Select start date';
      if (this.state.startDate && !this.state.endDate) {
        status = `${this._formatDisplayDate(this.state.startDate)}${sep}…`;
      } else if (this.state.startDate && this.state.endDate) {
        status = `${this._formatDisplayDate(this.state.startDate)}${sep}${this._formatDisplayDate(this.state.endDate)}`;
      }
      $footer.append(`<div class="gdp-range-status">${status}</div>`);
    }

    if (hasTime) {
      const $btnDone = $('<button type="button" class="gdp-btn-done">Done</button>');
      $btnDone.on('click', (e) => {
        e.stopPropagation();
        this.hide();
      });
      $footer.append($btnDone);
    }

    return $footer;
  }

  _selectDate(date) {
    if (this.config.range) {
      this._selectRangeDate(date);
      return;
    }

    if (date) this.state.currentDate = date;
    this._syncInputValue();
    if (this.config.onSelect) this.config.onSelect(this.state.currentDate, this);
    if (!this.config.components.hours && !this.config.components.minutes) {
      this.hide();
    }
    this._updateContent();
  }

  _selectRangeDate(date) {
    const result = selectRangeDate(this, date);
    this._syncInputValue();

    if (result === 'partial') {
      this._updateContent();
      return;
    }

    if (this.config.onSelect) this.config.onSelect(this._getRangePayload(), this);

    const hasTime = this.config.components.hours || this.config.components.minutes;
    if (!hasTime) {
      this.hide();
    } else {
      this._updateContent();
    }
  }

  val(value) {
    if (this.config.range) {
      if (value === undefined) return this._getRangePayload();
      const parsed = this._parseRangeValue(value, this.state.currentDate);
      this.state.startDate = parsed.startDate;
      this.state.endDate = parsed.endDate;
      this.state.rangeStep = parsed.rangeStep;
      this.state.currentDate = parsed.currentDate;
      this.state.viewDate = new Date(parsed.currentDate);
      this.state.hoverDate = null;
      this._syncInputValue();
      if (this.state.isOpen) this._updateContent();
      return this;
    }

    if (value === undefined) return this.state.currentDate;
    if (value === null) {
      this.state.currentDate = null;
    } else {
      this.state.currentDate = value instanceof Date ? value : new Date(value);
      this.state.viewDate = new Date(this.state.currentDate);
    }
    this._syncInputValue();
    return this;
  }

  showYear(enabled) {
    this.config.components.year = enabled;
    this._handleConfigChange();
    return this;
  }
  showMonth(enabled) {
    this.config.components.month = enabled;
    this._handleConfigChange();
    return this;
  }
  showDate(enabled) {
    this.config.components.date = enabled;
    this._handleConfigChange();
    return this;
  }
  showHours(enabled) {
    this.config.components.hours = enabled;
    this._handleConfigChange();
    return this;
  }
  showMinutes(enabled) {
    this.config.components.minutes = enabled;
    this._handleConfigChange();
    return this;
  }
  showSeconds(enabled) {
    this.config.components.seconds = enabled;
    this._handleConfigChange();
    return this;
  }
  showTime(enabled) {
    this.config.components.hours = enabled;
    this.config.components.minutes = enabled;
    this._handleConfigChange();
    return this;
  }
  showCompactTime(enabled) {
    this.config.compactTime = enabled;
    this._handleConfigChange();
    return this;
  }

  setDateFilter(fn) {
    this.config.filters.date = fn;
    this._handleConfigChange();
    return this;
  }
  setMonthFilter(fn) {
    this.config.filters.month = fn;
    this._handleConfigChange();
    return this;
  }
  setYearFilter(fn) {
    this.config.filters.year = fn;
    this._handleConfigChange();
    return this;
  }
  setHourFilter(fn) {
    this.config.filters.hour = fn;
    this._handleConfigChange();
    return this;
  }
  setMinuteFilter(fn) {
    this.config.filters.minute = fn;
    this._handleConfigChange();
    return this;
  }
  setSecondFilter(fn) {
    this.config.filters.second = fn;
    this._handleConfigChange();
    return this;
  }

  showYearValue(enabled) {
    this.config.display.year = enabled;
    this._handleConfigChange();
    return this;
  }
  showMonthValue(enabled) {
    this.config.display.month = enabled;
    this._handleConfigChange();
    return this;
  }
  showDateValue(enabled) {
    this.config.display.date = enabled;
    this._handleConfigChange();
    return this;
  }
  showHoursValue(enabled) {
    this.config.display.hours = enabled;
    this._handleConfigChange();
    return this;
  }
  showMinutesValue(enabled) {
    this.config.display.minutes = enabled;
    this._handleConfigChange();
    return this;
  }
  showSecondsValue(enabled) {
    this.config.display.seconds = enabled;
    this._handleConfigChange();
    return this;
  }

  _handleConfigChange() {
    const initialView = this._getInitialView();
    if (this.state.view === Constants.Views.DAYS && !this.config.components.date) this.state.view = initialView;
    if (this.state.view === Constants.Views.MONTHS && !this.config.components.month) this.state.view = initialView;
    if (this.state.view === Constants.Views.YEARS && !this.config.components.year) this.state.view = initialView;

    this._syncInputValue();
    if (this.state.isOpen) this._updateContent();
  }

  destroy() {
    const $ = window.jQuery;
    this.hide();
    if (this._registryKey) {
      genericDatepickerInstances.unregister(this._registryKey);
      this._registryKey = null;
    }
    $(document).off(this.namespace);
    this.$input.off(this.namespace);
    this.$input.removeClass('gdp-input').removeAttr('readonly');
    this.$input.removeData('generic-datepicker');
  }
}
